import { useCallback, useEffect, useState } from "react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Mail, Trash2, Reply, Send, X, CheckCircle2 } from "lucide-react";

const ReplyModal = ({ contact, onClose, onSuccess }) => {
  const [subject, setSubject] = useState(
    `Réponse à ton message — Amyu ♡`
  );
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!body.trim()) return toast.error("Écris ton message avant d'envoyer");
    setSending(true);
    try {
      await adminApi.post(`/admin/contacts/${contact.id}/reply`, {
        subject: subject.trim(),
        body: body.trim(),
      });
      toast.success(`Réponse envoyée à ${contact.email}`);
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Envoi impossible");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="reply-modal"
    >
      <div
        className="bg-white rounded-[28px] p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-coral font-medium">
              Répondre à
            </div>
            <div className="font-display text-2xl">{contact.name}</div>
            <div className="text-sm text-amyu-muted">{contact.email}</div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-amyu-cream hover:bg-amyu-cream/70 inline-flex items-center justify-center"
          >
            <X size={16} />
          </button>
        </div>

        <div className="bg-amyu-cream rounded-2xl p-3 text-sm mb-4">
          <div className="text-[10px] uppercase tracking-[0.16em] text-amyu-muted mb-1">
            Message original
          </div>
          <p className="whitespace-pre-wrap text-amyu-ink">{contact.message}</p>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-amyu-muted">
              Objet
            </label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="reply-subject-input"
              className="mt-1.5 w-full bg-amyu-cream rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-coral/30"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-amyu-muted">
              Message
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={7}
              placeholder="Bonjour, merci pour ton message..."
              data-testid="reply-body-input"
              className="mt-1.5 w-full bg-amyu-cream rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-coral/30 resize-none"
            />
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-full text-sm bg-amyu-cream hover:bg-amyu-cream/70"
          >
            Annuler
          </button>
          <button
            onClick={send}
            disabled={sending || !body.trim()}
            data-testid="reply-send-btn"
            className="btn-coral inline-flex items-center gap-2 disabled:opacity-60"
          >
            {sending ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Send size={14} />
            )}
            {sending ? "Envoi..." : "Envoyer"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function AdminContacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchContacts = useCallback(() => {
    setLoading(true);
    adminApi
      .get("/admin/contacts")
      .then((r) => setContacts(r.data.contacts))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const deleteContact = async (c) => {
    if (!window.confirm(`Supprimer le message de ${c.name} ?`)) return;
    setDeletingId(c.id);
    try {
      await adminApi.delete(`/admin/contacts/${c.id}`);
      setContacts((prev) => prev.filter((x) => x.id !== c.id));
      toast.success("Message supprimé");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setDeletingId(null);
    }
  };

  const deleteAll = async () => {
    if (
      !window.confirm(
        `⚠️ Supprimer TOUS les messages (${contacts.length}) ?\nAction irréversible.`
      )
    )
      return;
    try {
      const r = await adminApi.delete("/admin/contacts");
      toast.success(`${r.data.deleted_count} message(s) supprimé(s)`);
      fetchContacts();
    } catch {
      toast.error("Erreur");
    }
  };

  return (
    <div data-testid="admin-contacts-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-4xl">Messages</h1>
          <p className="text-amyu-muted mt-2">
            {contacts.length} message{contacts.length > 1 ? "s" : ""}
          </p>
        </div>
        {contacts.length > 0 && (
          <button
            onClick={deleteAll}
            data-testid="contacts-delete-all"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm bg-white border border-coral/40 text-coral hover:bg-coral hover:text-white transition-colors"
          >
            <Trash2 size={14} /> Tout supprimer
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="animate-spin text-coral" />
        </div>
      ) : contacts.length === 0 ? (
        <div className="text-center text-amyu-muted py-20 bg-white rounded-[28px] mt-6">
          Aucun message pour l&apos;instant.
        </div>
      ) : (
        <div className="mt-6 space-y-3" data-testid="admin-contacts-list">
          {contacts.map((c) => (
            <article
              key={c.id}
              data-testid={`admin-contact-${c.id}`}
              className="bg-white rounded-[20px] p-5"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-medium">{c.name}</div>
                    {c.status === "replied" && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] font-medium text-amyu-pistachio bg-amyu-pistachio/20 rounded-full px-2 py-0.5">
                        <CheckCircle2 size={10} /> Répondu
                      </span>
                    )}
                  </div>
                  <a
                    href={`mailto:${c.email}`}
                    className="text-sm text-coral hover:underline inline-flex items-center gap-1"
                  >
                    <Mail size={12} /> {c.email}
                  </a>
                </div>
                <div className="text-xs text-amyu-muted text-right">
                  {new Date(c.created_at).toLocaleString("fr-FR")}
                </div>
              </div>

              <p className="mt-3 text-sm bg-amyu-cream rounded-2xl p-3 whitespace-pre-wrap">
                {c.message}
              </p>

              {c.replies && c.replies.length > 0 && (
                <details className="mt-3">
                  <summary className="text-xs uppercase tracking-[0.16em] text-amyu-muted cursor-pointer">
                    {c.replies.length} réponse{c.replies.length > 1 ? "s" : ""} envoyée{c.replies.length > 1 ? "s" : ""}
                  </summary>
                  <div className="mt-2 space-y-2">
                    {c.replies.map((r) => (
                      <div key={r.sent_at} className="bg-amyu-pistachio/15 rounded-2xl p-3 text-sm">
                        <div className="text-[10px] uppercase tracking-[0.16em] text-amyu-muted mb-1">
                          {new Date(r.sent_at).toLocaleString("fr-FR")}
                          {" · "}
                          {r.subject}
                        </div>
                        <p className="whitespace-pre-wrap">{r.body}</p>
                      </div>
                    ))}
                  </div>
                </details>
              )}

              <div className="mt-4 flex flex-wrap gap-2 justify-end">
                <button
                  onClick={() => setReplyingTo(c)}
                  data-testid={`contact-reply-${c.id}`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm bg-coral text-white hover:bg-coral/90 transition-colors"
                >
                  <Reply size={14} /> Répondre
                </button>
                <button
                  onClick={() => deleteContact(c)}
                  disabled={deletingId === c.id}
                  data-testid={`contact-delete-${c.id}`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm bg-white border border-coral/40 text-coral hover:bg-coral hover:text-white transition-colors disabled:opacity-40"
                >
                  {deletingId === c.id ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Trash2 size={14} />
                  )}
                  Supprimer
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {replyingTo && (
        <ReplyModal
          contact={replyingTo}
          onClose={() => setReplyingTo(null)}
          onSuccess={fetchContacts}
        />
      )}
    </div>
  );
}
